import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Mobile-friendly searchable combobox built on shadcn's cmdk primitives.
 *
 * Props:
 *  - value         current selected value (string)
 *  - onChange      callback(newValue)
 *  - options       array of strings OR { value, label } — strings work as both
 *  - placeholder   trigger placeholder text
 *  - searchPlaceholder text shown inside the dropdown filter input
 *  - emptyMessage  text shown when no options match the search
 *  - disabled      disables the trigger + tooltip via `disabledMessage`
 *  - disabledMessage what to show in the trigger when disabled
 *  - testId        base data-testid used on trigger + popover items
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Selecciona una opción",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Sin resultados.",
  disabled = false,
  disabledMessage,
  testId = "searchable-select",
  className,
}) {
  const [open, setOpen] = useState(false);
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const selected = normalized.find((o) => o.value === value);
  // For edits of legacy listings the stored value may not match any option
  // (e.g. free-text "toyota" pre-dropdown). Show the raw value so the host
  // still sees what's saved — they can always pick a canonical option to
  // upgrade it.
  const fallbackLabel = value ? String(value) : "";
  const triggerLabel = disabled
    ? disabledMessage || placeholder
    : selected?.label || fallbackLabel || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-disabled={disabled}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className={cn(
            "w-full justify-between mt-1 font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className={cn("truncate text-left", !selected && !!fallbackLabel && "text-foreground")}>{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-h-[60vh]"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            data-testid={`${testId}-search`}
          />
          <CommandList className="max-h-[50vh] overflow-y-auto">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {normalized.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${opt.value}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
